import { TextField } from "@mui/material";
import MainSwitch from "components/MainSwitch/MainSwitch.view";
import { useStyles } from "../../useRCASettingsColumns.styles";
import { useContext, useMemo, useState } from "react";
import { IRCASettings } from "entities/RCA/RCASettingsEntity/RCASettingsEntityInterfaces";
import { useTranslation } from "react-i18next";
import NewMainSelect from "components/NewMainSelect/NewMainSelect.view";
import { RCASettingsContext } from "contexts/RCASettingsContext.ts/RCASettingsContext";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";

const RCASettingsFrequencyTrigger = ({ row, canEdit }: { row: IRCASettings; canEdit: boolean | undefined }) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { rcaSettingsChanges, setRcaSettingsChanges } = useContext(RCASettingsContext);
    const currentChanges = useMemo(
        () =>
            rcaSettingsChanges.find(
                (rcaSetting) =>
                    rcaSetting.production_line_id === row.production_line_id &&
                    rcaSetting.failure_type.failure_type_id === row.failure_type.failure_type_id,
            ),
        [rcaSettingsChanges, row],
    );
    const [value, setValue] = useState<number | undefined>(
        currentChanges?.rca_frequency_trigger?.value || row.rca_frequency_trigger.value,
    );
    const [frequencyUnitId, setFrequencyUnitId] = useState<number | undefined>(
        currentChanges?.rca_frequency_trigger?.rca_frequency_trigger_unit_id || undefined,
    );
    const [check, setCheck] = useState<boolean | undefined>(currentChanges?.rca_frequency_trigger?.is_enabled);
    const selectionRequired = useMemo(() => check && !frequencyUnitId, [check, frequencyUnitId]);

    return (
        <div className={classes.mainCell}>
            <MainSwitch
                checked={check === undefined ? row.rca_frequency_trigger.is_enabled : check}
                onChange={(event) => {
                    setRcaSettingsChanges((prev) => {
                        let prevRcaSettings = [...prev];
                        const existingSettings = prevRcaSettings.findIndex(
                            (rcaSetting) =>
                                rcaSetting.production_line_id === row.production_line_id &&
                                rcaSetting.failure_type.failure_type_id === row.failure_type.failure_type_id,
                        );
                        if (existingSettings === -1) {
                            return [
                                ...prevRcaSettings,
                                {
                                    ...row,
                                    rca_frequency_trigger: {
                                        ...row.rca_frequency_trigger,
                                        is_enabled: event.target.checked,
                                    },
                                },
                            ];
                        } else {
                            prevRcaSettings[existingSettings] = {
                                ...prevRcaSettings[existingSettings],
                                rca_frequency_trigger: {
                                    ...prevRcaSettings[existingSettings].rca_frequency_trigger,
                                    is_enabled: event.target.checked,
                                },
                            };
                            return prevRcaSettings;
                        }
                    });
                    setCheck(event.target.checked);
                }}
                disabled={!canEdit}
            />
            <TextField
                className={classes.textField}
                disabled={(check === undefined ? !row.rca_frequency_trigger.is_enabled : !check) || !canEdit}
                onChange={(event) => {
                    setRcaSettingsChanges((prev) => {
                        let prevRcaSettings = [...prev];
                        const existingSettings = prevRcaSettings.findIndex(
                            (rcaSetting) =>
                                rcaSetting.production_line_id === row.production_line_id &&
                                rcaSetting.failure_type.failure_type_id === row.failure_type.failure_type_id,
                        );
                        if (existingSettings === -1) {
                            return [
                                ...prevRcaSettings,
                                {
                                    ...row,
                                    rca_frequency_trigger: {
                                        ...row.rca_frequency_trigger,
                                        value: Number(event.target.value),
                                    },
                                },
                            ];
                        } else {
                            prevRcaSettings[existingSettings] = {
                                ...prevRcaSettings[existingSettings],
                                rca_frequency_trigger: {
                                    ...prevRcaSettings[existingSettings].rca_frequency_trigger,
                                    value: Number(event.target.value),
                                },
                            };
                            return prevRcaSettings;
                        }
                    });
                    setValue(Number(event.target.value));
                }}
                variant="outlined"
                size="small"
                value={value}
            />
            {t("rca.settings.per")}
            <NewMainSelect
                className={selectionRequired ? classes.requiredBorderSelect : classes.select}
                fullWidth={false}
                size="small"
                placeholder={t("rca.settings.frequency_trigger_dropdown.select_frequency")}
                options={row.frequency_trigger_unit_options.map((option) => ({
                    id: option.rca_frequency_trigger_unit_id,
                    name: translateDefaultEntity(
                        i18n.language,
                        option,
                        "name",
                        DefaultTranslationFieldsInTable.RCA_FREQUENCY_TRIGGER_UNIT,
                    ),
                }))}
                disabled={(check === undefined ? !row.rca_frequency_trigger.is_enabled : !check) || !canEdit}
                value={frequencyUnitId || row.rca_frequency_trigger?.rca_frequency_trigger_unit_id || ""}
                onChange={(selected) => {
                    setRcaSettingsChanges((prev) => {
                        let prevRcaSettings = [...prev];
                        const existingSettings = prevRcaSettings.findIndex(
                            (rcaSetting) =>
                                rcaSetting.production_line_id === row.production_line_id &&
                                rcaSetting.failure_type.failure_type_id === row.failure_type.failure_type_id,
                        );
                        if (existingSettings === -1) {
                            return [
                                ...prevRcaSettings,
                                {
                                    ...row,
                                    rca_frequency_trigger: {
                                        ...row.rca_frequency_trigger,
                                        rca_frequency_trigger_unit_id: Number(selected),
                                    },
                                },
                            ];
                        } else {
                            prevRcaSettings[existingSettings] = {
                                ...prevRcaSettings[existingSettings],
                                rca_frequency_trigger: {
                                    ...prevRcaSettings[existingSettings].rca_frequency_trigger,
                                    rca_frequency_trigger_unit_id: Number(selected),
                                },
                            };
                            return prevRcaSettings;
                        }
                    });
                    setFrequencyUnitId(Number(selected));
                }}
                multiple={false}
            />
        </div>
    );
};

export default RCASettingsFrequencyTrigger;
